import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Users, Award, Clock, Phone, MapPin, Truck, TreePine, Star, ChevronRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import grassDepotLogo from '@/assets/grass-depot-logo.png';
import SEO from '@/components/SEO';

const AboutPage = () => {
  const { language, t } = useLanguage();

  const seoTitle = language === 'en' 
    ? 'About The Grass Depot LLC | Family-Owned Landscaping Company Texas'
    : 'Acerca de The Grass Depot LLC | Empresa de Paisajismo Familiar Texas';
  
  const seoDescription = language === 'en'
    ? 'Learn about The Grass Depot LLC, family-owned grass delivery and installation company serving Montgomery County TX since 2025.'
    : 'Conozca The Grass Depot LLC, empresa familiar de entrega e instalación de césped sirviendo al Condado Montgomery TX desde 2025.';
  
  const seoKeywords = language === 'en'
    ? 'about grass depot, family owned landscaping texas, landscaping company montgomery county, landscaping history texas, experienced landscapers'
    : 'acerca grass depot, paisajismo familiar texas, empresa paisajismo montgomery county, historia paisajismo texas, paisajistas experimentados';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "The Grass Depot LLC",
    "description": seoDescription,
    "foundingDate": "2024",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "TX",
      "addressLocality": "Montgomery County"
    },
    "areaServed": "Montgomery County TX",
    "founder": {
      "@type": "Person",
      "name": "Andrew Kelley"
    }
  };

  const stats = [
    {
      number: '15+',
      label: language === 'en' ? 'Years Experience' : 'Años de Experiencia',
      icon: Clock
    },
    {
      number: '2000+',
      label: language === 'en' ? 'Projects Completed' : 'Proyectos Completados',
      icon: CheckCircle
    },
    {
      number: '8',
      label: language === 'en' ? 'Cities Served' : 'Ciudades Servidas',
      icon: MapPin
    },
    {
      number: '100%',
      label: language === 'en' ? 'Satisfaction Rate' : 'Tasa de Satisfacción',
      icon: Star
    }
  ];

  const values = [
    {
      title: language === 'en' ? 'Quality First' : 'Calidad Primero',
      description: language === 'en' 
        ? 'We source only the finest materials and maintain the highest standards in every project'
        : 'Obtenemos solo los mejores materiales y mantenemos los más altos estándares en cada proyecto',
      icon: Award
    },
    {
      title: language === 'en' ? 'Local Expertise' : 'Experiencia Local',
      description: language === 'en'
        ? 'Deep understanding of Texas climate, soil conditions, and local landscaping needs'
        : 'Comprensión profunda del clima de Texas, condiciones del suelo y necesidades locales de paisajismo',
      icon: TreePine
    },
    {
      title: language === 'en' ? 'Customer Service' : 'Servicio al Cliente',
      description: language === 'en'
        ? 'Dedicated to exceeding expectations with personalized service and ongoing support'
        : 'Dedicados a superar expectativas con servicio personalizado y apoyo continuo',
      icon: Users
    },
    {
      title: language === 'en' ? 'Reliable Delivery' : 'Entrega Confiable',
      description: language === 'en'
        ? 'On-time delivery and professional installation services you can count on'
        : 'Entrega puntual y servicios de instalación profesional en los que puedes confiar',
      icon: Truck
    }
  ];


  const breadcrumbs = [
    { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
    { name: language === 'en' ? 'About' : 'Nosotros', url: language === 'en' ? '/en/about' : '/es/nosotros' }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/about' : '/es/nosotros'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={breadcrumbs}
      />
      <Header />
      
      <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 animate-pulse"></div>
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-white/80">
              {language === 'en' ? 'About The Grass Depot' : 'Acerca de The Grass Depot'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 animate-fade-in delay-200">
              {language === 'en' 
                ? 'Your trusted partner for premium landscaping materials and professional installation services in Texas'
                : 'Tu socio de confianza para materiales de paisajismo premium y servicios de instalación profesional en Texas'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in delay-400">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base hover:bg-white/30 transition-all hover-scale">
                <Clock className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Founded 2024' : 'Fundado 2024'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base hover:bg-white/30 transition-all hover-scale">
                <Award className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Family Owned' : 'Propiedad Familiar'}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-primary" />
                  </div>
                  <div className="text-4xl font-bold text-primary mb-2">{stat.number}</div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src={grassDepotLogo} 
                  alt="The Grass Depot"
                  className="w-full max-w-md mx-auto"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  {language === 'en' ? 'Our Story' : 'Nuestra Historia'}
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    {language === 'en'
                      ? 'Founded in 2024 by Andrew Kelley, The Grass Depot LLC was born from a simple but powerful vision: to provide high-quality turf solutions and exceptional service to homeowners and businesses in the Montgomery County area.'
                      : 'Fundado en 2024 por Andrew Kelley, The Grass Depot LLC nació de una visión simple pero poderosa: proporcionar soluciones de césped de alta calidad y servicio excepcional a propietarios y empresas en el área del Condado de Montgomery.'
                    }
                  </p>
                  <p>
                    {language === 'en'
                      ? 'Andrew started The Grass Depot with a passion for helping people create beautiful, sustainable outdoor spaces. From sod installation to expert advice on grass varieties, our goal is to make every lawn we touch healthier, greener, and easier to maintain.'
                      : 'Andrew comenzó The Grass Depot con una pasión por ayudar a las personas a crear espacios exteriores hermosos y sostenibles. Desde instalación de césped hasta asesoramiento experto sobre variedades de césped, nuestro objetivo es hacer que cada jardín que tocamos sea más saludable, más verde y más fácil de mantener.'
                    }
                  </p>
                  <p>
                    {language === 'en'
                      ? 'At The Grass Depot, we believe in quality, integrity, and community. We take pride in delivering not just top-notch products, but also personalized service that makes your landscaping projects smooth and successful. Whether you\'re a homeowner upgrading your yard or a contractor looking for reliable turf supplies, we\'re here to make your vision a reality.'
                      : 'En The Grass Depot, creemos en la calidad, integridad y comunidad. Nos enorgullecemos de entregar no solo productos de primera calidad, sino también servicio personalizado que hace que sus proyectos de paisajismo sean fluidos y exitosos. Ya sea que sea un propietario mejorando su patio o un contratista buscando suministros confiables de césped, estamos aquí para hacer realidad su visión.'
                    }
                  </p>
                  <p>
                    {language === 'en'
                      ? 'Your lawn is our passion — let\'s grow something great together.'
                      : 'Su césped es nuestra pasión: crezcamos algo grandioso juntos.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Our Values' : 'Nuestros Valores'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'The principles that guide everything we do'
                : 'Los principios que guían todo lo que hacemos'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-glow transition-all duration-300">
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground">
                      {value.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' 
              ? 'Ready to Work With Us?' 
              : '¿Listo para Trabajar con Nosotros?'
            }
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Experience the difference that commitment to quality and fresh grass makes.'
              : 'Experimenta la diferencia que hace el compromiso con la calidad y césped fresco.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/quote`}>
              <Button variant="hero" size="lg">
                {language === 'en' ? 'Get Started Today' : 'Comenzar Hoy'}
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-secondary-foreground/20 hover:bg-secondary-foreground/10">
                <Phone className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Call Us' : 'Llámanos'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;